import { Elements } from "@stripe/react-stripe-js";
import {
  Box, Typography, Stack, Link, Container,
} from '@mui/material';
import React from 'react';
import getPageMetaTags from "../lib/meta/_getMetaTags";
import getPricePlans from "../lib/pricing/_getPricePlans";
import PremiumPlan from "../src/components/Pricing/PremiumPlan";
import { loadStripe } from "@stripe/stripe-js";
import PremiumFeatures from "../src/components/Pricing/Features";
import FAQs from "../src/components/Pricing/FAQs";
import { dataConstants } from "../src/constants";
import { colors, shadows, radii } from "../src/utils/designTokens";
import getStockTabs from '../lib/stock/_getStockTabsData';
import { parseTabData } from "../src/utils/helpers";

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY);


const Testimonials = ({ quotes }) => {
  if (!quotes?.length) return null;
  return (
    <Stack
      direction={{ md: "row", xs: "column" }}
      spacing={3}
      justifyContent="center"
      alignItems="stretch"
      sx={{ mb: { xs: 6, md: 8 } }}
    >
      {quotes.map((d, i) => (
        <Box
          key={i}
          component={d?.link ? "a" : "div"}
          href={d?.link || undefined}
          target={d?.link ? "_blank" : undefined}
          rel={d?.link ? "noreferrer nofollow" : undefined}
          sx={{
            flex: 1,
            textAlign: 'center',
            textDecoration: 'none',
            color: 'inherit',
            p: { xs: 2.5, md: 3 },
            borderRadius: `${radii.md}px`,
            bgcolor: colors.neutral[50],
            border: `1px solid ${colors.neutral[200]}`,
            transition: 'border-color 200ms ease, box-shadow 200ms ease',
            '&:hover': {
              borderColor: colors.neutral[300],
              boxShadow: shadows.sm,
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: colors.neutral[800],
              lineHeight: 1.6,
              mb: 1,
            }}
            dangerouslySetInnerHTML={{ __html: d.text }}
          />
          {d.author && (
            <Typography variant="caption" sx={{ color: colors.neutral[500] }}>
              {"— "}{d.author}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
};

const TrustFooter = () => (
  <Box
    sx={{
      textAlign: 'center',
      pt: { xs: 6, md: 8 },
      pb: { xs: 8, md: 10 },
      borderTop: `1px solid ${colors.neutral[200]}`,
    }}
  >
    <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 1.5 }}>
      For payment queries, contact us at{' '}
      <Link href="mailto:contact@grufity.com?subject=Regarding Payment" underline="hover" sx={{ color: colors.accent.main, fontWeight: 500 }}>
        contact@grufity.com
      </Link>
    </Typography>
    <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 3, display: 'block' }}>
      Not happy? Email us at{' '}
      <Link href="mailto:support@grufity.com?subject=Customer Feedback" underline="hover" sx={{ color: colors.accent.main, fontWeight: 500 }}>
        support@grufity.com
      </Link>
    </Typography>
    <Stack
      direction="row"
      spacing={2}
      justifyContent="center"
      divider={
        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.neutral[300], alignSelf: 'center' }} />
      }
    >
      <Link href="/terms" underline="hover" sx={{ color: colors.neutral[500], fontSize: '0.8rem' }}>Terms</Link>
      <Link href="/privacy" underline="hover" sx={{ color: colors.neutral[500], fontSize: '0.8rem' }}>Privacy</Link>
      <Link href="/contact" underline="hover" sx={{ color: colors.neutral[500], fontSize: '0.8rem' }}>Contact</Link>
    </Stack>
  </Box>
);


const Pricing = ({ pricePlans, pricing_quotes }) => {
  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ px: { xs: 2.5, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', pt: { xs: 6, md: 6 }, pb: { xs: 4, md: 4 } }}>
          <Typography
            variant="overline"
            component="div"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              color: colors.accent.main,
              mb: 2,
            }}
          >
            PRICING
          </Typography>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              lineHeight: 1.2,
              letterSpacing: '-0.025em',
              color: colors.neutral[900],
              mb: 2,
            }}
          >
            Choose Your Plan
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.neutral[500],
              fontSize: { xs: '1rem', md: '1.1rem' },
              maxWidth: 520,
              mx: 'auto',
              lineHeight: 1.65,
            }}
          >
            Unlock premium stock analysis, fund holdings, screener tools,
            and real-time market insights.
          </Typography>
        </Box>
        <Elements stripe={stripePromise}>
          <PremiumPlan pricePlans={pricePlans} />
        </Elements>
        <Testimonials quotes={pricing_quotes?.quotes} />
        <PremiumFeatures />
        <FAQs pricePlans={pricePlans} />
        <TrustFooter />
      </Container>
    </Box>
  );
};

export async function getStaticProps() {
  const { plans } = (await getPricePlans()) || {};
  const meta_tags = (await getPageMetaTags('/pricing', false)) || {};
  const { getMetaTags } = meta_tags || {};
  let pricing_quotes = null;
  try {
    const { getStockTabs: tabData } = (await getStockTabs("AAPL", "PRICING")) || {};
    let quotesSection = tabData?.length > 0 ? tabData.find(d => d.section_code === "pricing_quotes") : null;
    quotesSection = quotesSection ? parseTabData(quotesSection) : null;
    pricing_quotes = quotesSection?.settings || null;
  } catch (_) {
    // Quotes are non-critical — degrade gracefully
  }

  return {
    props: {
      pricePlans: plans || {},
      pricing_quotes: pricing_quotes || {},
      meta_tags: getMetaTags?.tags || null,
    },
    revalidate: dataConstants.REVALIDATE_IN.DEFAULT_HOURS,
  };
}

export default Pricing;
